<template>
  <div>
    <div
      ref="carouselRef"
      class="boost-opportunities-carousel tw:flex tw:gap-4 tw:overflow-x-auto tw:pt-4 tw:pb-6"
      :class="{
        'boost-opportunities-carousel--dragging': isDragging,
        'tw:scroll-smooth tw:snap-x tw:snap-mandatory': !isDragging,
      }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp">
      <div
        v-for="card in props.cards"
        :key="`${card.dateRangeStart}-${card.dateRangeEnd}-${card.opportunityType}`"
        class="tw:shrink-0 tw:w-[280px] tw:bg-white tw:border tw:border-color-white tw:rounded-xl tw:shadow-[0_0_16px_0_rgba(0,0,0,0.15)] tw:overflow-hidden"
        :class="{ 'tw:snap-start': !isDragging }">
        <div class="tw:p-4">
          <div class="tw:mb-2">
            <span
              class="tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:font-bold tw:px-2 tw:py-1 tw:rounded-md tw:bg-orange-200">
              <img :src="getOpportunityIcon(card.opportunityType)" alt="" aria-hidden="true" class="tw:w-3 tw:h-3" />
              {{ card.opportunityType === 'HOT_OPPORTUNITY' ? $t('rc_top_opportunity') : $t('rc_good_opportunity') }}
            </span>
          </div>

          <p class="tw:text-xl tw:font-bold tw:pb-4">
            {{ formatDateRange(card.dateRangeStart, card.dateRangeEnd) }}
          </p>
          <p class="tw:text-xxs tw:font-bold tw:mt-3 tw:text-gray-700">
            {{ $t('rc_top_position_share_header') }}
          </p>
        </div>

        <div class="tw:border-t tw:border-gray-300">
          <button
            type="button"
            class="tw:flex tw:w-full tw:justify-between tw:items-center tw:px-4 tw:py-3 tw:border-b tw:border-gray-300 tw:cursor-pointer tw:hover:bg-gray-50 tw:text-left"
            :data-testid="`boost-opportunity-cta-${card.dateRangeStart}-${card.dateRangeEnd}-a`"
            @click="goToSmartBoost(card, card.boostCpaLevelA)">
            <div>
              <p class="tw:text-base tw:font-bold">
                {{ formatPercentage(card.boostCpaLevelA, 0, 0) }} {{ $t('rc_smartboost_history_commission') }}
              </p>
              <p class="tw:text-xs" :class="getShareIncreaseLabel(card, card.boostATpShareIncrease).colorClass">
                {{ getShareIncreaseLabel(card, card.boostATpShareIncrease).text }}
              </p>
            </div>
            <div class="tw:bg-blue-700 tw:rounded-md tw:w-9 tw:h-9 tw:flex tw:items-center tw:justify-center">
              <ChevronRight class="tw:fill-white tw:size-5" />
            </div>
          </button>

          <button
            type="button"
            class="tw:flex tw:w-full tw:justify-between tw:items-center tw:px-4 tw:py-3 tw:cursor-pointer tw:hover:bg-gray-50 tw:text-left"
            :data-testid="`boost-opportunity-cta-${card.dateRangeStart}-${card.dateRangeEnd}-b`"
            @click="goToSmartBoost(card, card.boostCpaLevelB)">
            <div>
              <p class="tw:text-base tw:font-bold">
                {{ formatPercentage(card.boostCpaLevelB, 0, 0) }} {{ $t('rc_smartboost_history_commission') }}
              </p>
              <p class="tw:text-xs" :class="getShareIncreaseLabel(card, card.boostBTpShareIncrease).colorClass">
                {{ getShareIncreaseLabel(card, card.boostBTpShareIncrease).text }}
              </p>
            </div>
            <div class="tw:bg-blue-700 tw:rounded-md tw:w-9 tw:h-9 tw:flex tw:items-center tw:justify-center">
              <ChevronRight class="tw:fill-white tw:size-5" />
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'

import { useFormatting } from '@/composables/useFormatting'
import { useBoostOpportunityNavigation } from '@/composables/useBoostOpportunityNavigation'

const props = defineProps({
  cards: {
    type: Array,
    default: () => [],
  },
})

const starIcon = new URL('/src/assets/img/rate-connect/star-icon.svg', import.meta.url)
const circleIcon = new URL('/src/assets/img/rate-connect/circle-icon.svg', import.meta.url)
const { formatDate, formatPercentage } = useFormatting()
const { getShareIncreaseLabel, goToSmartBoost } = useBoostOpportunityNavigation()

const getOpportunityIcon = opportunityType => {
  return opportunityType === 'HOT_OPPORTUNITY' ? starIcon : circleIcon
}

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '-'
  const formattedStartDate = formatDate(startDate, { month: 'long', day: 'numeric' })
  const formattedEndDate = formatDate(endDate, { month: 'long', day: 'numeric' })

  if (startDate === endDate) return formattedStartDate
  return `${formattedStartDate} - ${formattedEndDate}`
}

const carouselRef = ref(null)
const isDragging = ref(false)

let dragStartX = 0
let dragScrollStart = 0

const canScrollHorizontally = el => el && el.scrollWidth > el.clientWidth

const onPointerDown = event => {
  if (event.button !== 0 || event.target.closest('button')) return

  const el = carouselRef.value
  if (!el || !canScrollHorizontally(el)) return

  isDragging.value = true
  dragStartX = event.clientX
  dragScrollStart = el.scrollLeft
  el.setPointerCapture(event.pointerId)
}

const onPointerMove = event => {
  if (!isDragging.value) return

  const el = carouselRef.value
  if (!el) return

  event.preventDefault()
  el.scrollLeft = dragScrollStart - (event.clientX - dragStartX)
}

const onPointerUp = event => {
  if (!isDragging.value) return

  const el = carouselRef.value
  isDragging.value = false

  if (el) {
    try {
      el.releasePointerCapture(event.pointerId)
    } catch {
      // pointer may already be released
    }
  }
}
</script>

<style scoped>
.boost-opportunities-carousel {
  cursor: grab;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-inline: 16px;
  margin-inline: -16px;
  scroll-padding-inline: 16px;
}

.boost-opportunities-carousel--dragging {
  cursor: grabbing;
  scroll-behavior: auto;
  scroll-snap-type: none;
  user-select: none;
}

.boost-opportunities-carousel::-webkit-scrollbar {
  display: none;
}
</style>
